import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Button,
  Container,
  Grid,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DateObject } from "react-multi-date-picker";
import { IOperator } from "../../../api/types/directory/IOperator";
import { IReportData } from "../../../api/types/reports/IReportInfo";
import { IReportParams } from "../../../api/types/reports/IReportParams";
import { ReportAction } from "../../../api/types/reports/ReportAction";
import { DayPicker } from "../../../components/picker/DayPicker";
import { MultiDateSelect } from "../../../components/select/MultiDateSelect";
import { SelectField } from "../../../components/select/SelectField";
import { SpacedContainerStyled } from "../../../components/styled/SpacedContainerStyled";
import { nightsDataSource } from "../../../components/template/data/nightsDataSource";
import { EditorMode } from "../../../components/template/types/EditorMode";
import { inputStyleVariant } from "../../../constants/interface";
import { PagePath } from "../../../navigation/routes";
import { fetchDepartureCity } from "../../../store/directory/thunks/fetchDepartureCity";
import { fetchDestinationCities } from "../../../store/directory/thunks/fetchDestinationCities";
import { fetchDestinationCountries } from "../../../store/directory/thunks/fetchDestinationCountries";
import { fetchOperators } from "../../../store/directory/thunks/fetchOperators";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { createTicketGridFromTemplate } from "../../../store/ticketGrids/thunks/createTicketGridFromTemplate";
import { createTicketGridTemplate } from "../../../store/ticketGrids/thunks/createTicketGridTemplate";
import { editTicketGridTemplate } from "../../../store/ticketGrids/thunks/editTicketGridTemplate";
import { ReportStatus } from "../../../store/reports/types/reportStatus";
import { canViewTicketGridChildTemplates } from "../../../utils/userPermissions";
import { AccommodationType } from "./types/AccommodationType";

const TICKET_GRID_TEMPLATE_TYPE = 1; // Авиа

interface Props {
  mode: EditorMode;
  source?: IReportData;
  onClose: () => void;
}

export const TicketGridEditor = ({ mode, source, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [name, setName] = useState(source?.name ?? "");
  const [operator, setOperator] = useState<IOperator[]>(
    source?.sOperators ?? [],
  );
  const [departureCity, setDepartureCity] = useState<number | undefined>(
    source?.city_from,
  );
  const [destinationCountry, setDestinationCountry] = useState<
    number | undefined
  >(source?.country);
  const [destinationCity, setDestinationCity] = useState<number | undefined>(
    source?.city_id,
  );
  const [tourDates, setTourDates] = useState<DateObject[]>(
    source?.dates.split(",").map((d) => {
      const dObj = new DateObject({
        date: d,
        format: "DD.MM.YYYY",
      });
      return dObj.parse(d);
    }) ?? [],
  );
  const [nights, setNights] = useState<number[]>(
    source?.nights.split(",").map((n) => parseInt(n, 10)) ?? [],
  );
  const [runTime, setRunTime] = useState(source?.schedule_time ?? "");
  const [runDays, setRunDays] = useState<number[]>(
    source
      ? source.schedule_days
          .split(",")
          .map((d) => parseInt(d, 10))
          .filter((val) => !isNaN(val))
      : [],
  );

  const loadingKeys = useAppSelector((state) => state.directory.loadingKeys);
  const operators = useAppSelector((state) => state.directory.operators ?? []);
  const departureCities = useAppSelector(
    (state) => state.directory.departureCities ?? [],
  );
  const destinationCountries = useAppSelector(
    (state) => state.directory.destinationCountries ?? [],
  );
  const destinationCities = useAppSelector(
    (s) => s.directory.destinationCities ?? [],
  );
  const loading = useAppSelector((s) => s.ticketGrids.ticketGridsCreating);

  const ticketGridsCreated = useAppSelector(
    (s) => s.ticketGrids.updated,
    (lhs, rhs) => lhs.join(",") === rhs.join(","),
  );
  const userType = useAppSelector((s) => s.user.info?.type);

  const canSave =
    name.length > 0 &&
    operator.length > 0 &&
    departureCity !== undefined &&
    destinationCountry !== undefined &&
    destinationCity !== undefined &&
    nights.length > 0 &&
    tourDates.length > 0;

  const operatorsLoading = loadingKeys.includes("operators");
  const departureCitiesDisabled = operatorsLoading || operator.length === 0;
  const destCountriesDisabled = departureCitiesDisabled || !departureCity;
  const destCityDisabled = destCountriesDisabled || !destinationCountry;

  useEffect(() => {
    if (mode === "create") dispatch(fetchOperators());
  }, []);

  useEffect(() => {
    if (ticketGridsCreated.length > 0) {
      const lastCreated = ticketGridsCreated[ticketGridsCreated.length - 1];
      if (mode === "create") {
        navigate(
          canViewTicketGridChildTemplates(userType)
            ? PagePath.ticketGrids.childTemplatesFor(lastCreated.id)
            : PagePath.ticketGrids.templates,
        );
      } else {
        clearForm();
        if (onClose) onClose();
      }
    }
  }, [ticketGridsCreated]);

  const clearDestinationCountryDependencies = () => {
    setDestinationCity(undefined);
  };
  const clearDepartureCityDependencies = () => {
    setDestinationCountry(undefined);
    clearDestinationCountryDependencies();
  };
  const clearOperatorDependencies = () => {
    setDepartureCity(undefined);
    clearDepartureCityDependencies();
  };

  const clearForm = () => {
    setName("");
    setOperator([]);
    clearOperatorDependencies();
    setTourDates([]);
    setNights([]);
    setRunTime("");
    setRunDays([]);
  };

  const submitTemplate = () => {
    if (!canSave) return;
    const params: IReportParams = {
      accommodation: AccommodationType.All,
      action:
        mode === "edit" ? ReportAction.editReport : ReportAction.createReport,
      city_from: departureCity,
      country: destinationCountry,
      dates: tourDates.join(","),
      hotels: "",
      exclude_hotels: 0,
      name,
      nights: nights.join(","),
      operators: operator.map((o) => o.id).join(","),
      schedule_days: runDays.join(","),
      schedule_time: runTime,
      special_order: "",
      tour_types: -1,
      type: TICKET_GRID_TEMPLATE_TYPE,
      tour_id: "",
      city_id: destinationCity,
    };
    if (mode === "create") dispatch(createTicketGridTemplate(params));
    if (mode === "clone") {
      if (source?.status == ReportStatus.template)
        dispatch(createTicketGridTemplate(params));
      else dispatch(createTicketGridFromTemplate(params));
    }
    if (mode === "edit" && source) {
      dispatch(editTicketGridTemplate({ id: source.id, ...params }));
    }
  };

  const onCancelPress = () => {
    if (mode === "create") clearForm();
    else if (onClose) onClose();
  };

  const onOperatorSelect = (val: IOperator[]) => {
    setOperator(val);
    if (val.length > 0) {
      dispatch(
        fetchDepartureCity({
          operatorId: 10,
        }),
      );
    }
  };

  const onDepartureCitySelect = (id: number) => {
    if (departureCity !== id) {
      if (operator.length > 0)
        dispatch(
          fetchDestinationCountries({
            operator: 10,
            departureCity: id,
            multiOperator: false,
          }),
        );
    }
    setDepartureCity(id);
  };

  const onDestinationCountrySelect = (id: number) => {
    if (destinationCountry !== id) {
      if (operator.length > 0 && departureCity) {
        dispatch(
          fetchDestinationCities({
            departureCity: departureCity,
            operator: 10,
            destinationCountry: id,
          }),
        );
      }
    }
    setDestinationCountry(id);
  };

  const onDestinationCitySelect = (id: number) => {
    setDestinationCity(id);
  };

  return (
    <Container maxWidth="md" component={"div"}>
      <Grid container spacing={1} rowSpacing={2} alignItems={"flex-end"}>
        {mode === "create" ? (
          <Grid item xs={12}>
            <h1>Создание билетной сетки</h1>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <TextField
            label="Название билетной сетки"
            required
            variant={inputStyleVariant}
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value ?? "")}
          />
        </Grid>
        <Grid item xs={8}>
          <Autocomplete
            multiple
            options={operators}
            loading={loadingKeys.includes("operators")}
            disabled={false}
            getOptionLabel={(option) => option.name}
            value={operator}
            isOptionEqualToValue={(lhs, rhs) => lhs.id === rhs.id}
            onChange={(_event, newValue) => {
              const uniqueMap = new Map<number, IOperator>();
              newValue.forEach((i) => uniqueMap.set(i.id, i));
              onOperatorSelect([...uniqueMap.values()]);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant={inputStyleVariant}
                label="Оператор"
                placeholder="Оператор"
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectField
            items={departureCities}
            required
            loading={loadingKeys.includes("departureCities")}
            label={"Город вылета"}
            value={departureCity}
            disabled={departureCitiesDisabled}
            onSelect={(val) => onDepartureCitySelect(val as number)}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectField
            items={destinationCountries}
            required
            disabled={destCountriesDisabled}
            label={"Страна назначения"}
            value={destinationCountry}
            loading={loadingKeys.includes("destinationCountries")}
            onSelect={(val) => onDestinationCountrySelect(val as number)}
          />
        </Grid>
        <Grid item xs={4}>
          <SelectField
            items={destinationCities}
            required
            disabled={destCityDisabled}
            label={"Город назначения"}
            value={destinationCity}
            loading={loadingKeys.includes("destinationCities")}
            onSelect={(val) => onDestinationCitySelect(val as number)}
          />
        </Grid>
        <Grid item xs={6}>
          <MultiDateSelect
            required
            value={tourDates}
            onChange={setTourDates}
            label={"Даты тура *"}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            options={nightsDataSource}
            isOptionEqualToValue={(option, value) => option === value}
            value={nights.map((n) => `${n}`)}
            onChange={(e, v) =>
              setNights(v.map((n) => parseInt(n, 10)).sort((a, b) => a - b))
            }
            getOptionLabel={(option) => option}
            defaultValue={[]}
            renderInput={(params) => (
              <TextField
                {...params}
                variant={inputStyleVariant}
                label="Количество ночей"
                placeholder="Количество ночей"
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <h5>Параметры запуска</h5>
          <DayPicker
            selected={runDays}
            onChange={(val) => setRunDays(val)}
            time={runTime}
            onTimeChange={(val) => setRunTime(val)}
          />
        </Grid>
        {mode === "create" ? (
          <Grid item xs={12}>
            <Alert severity="info">
              После создания билетной сетки, необходимо по каждому оператору
              проверить дополнительные фильтры и сохранить.
            </Alert>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <SpacedContainerStyled>
            <Button variant="text" onClick={onCancelPress}>
              {mode === "create" ? "Очистить" : "Отмена"}
            </Button>
            <LoadingButton
              loading={loading}
              disabled={!canSave || loading}
              variant="contained"
              onClick={() => submitTemplate()}
            >
              {mode === "clone" ? "Клонировать" : "Сохранить"}
            </LoadingButton>
          </SpacedContainerStyled>
        </Grid>
      </Grid>
    </Container>
  );
};
